// HelpScout Beacon utilities for error tracking and support
// Shared across the enrollment views (Verify, Pay, lti/Pay) and the LTI tool
// screens (Teacher Resources, Dictionary, Video Picker)
//
// TWA-273: a build that sets the Salesforce chat env vars (dev only for now) routes
// every call below to Salesforce Enhanced Chat instead (sf-chat.js).

import { getErrorMessage } from './error-messages'
import * as sfChat from './sf-chat'

const BEACON_ID = '99c004f4-81f5-469f-841d-a4e7ab4d7d6d'

/**
 * Creates a script element with the given content
 */
function createScriptTag(content) {
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.text = content
  return script
}

/**
 * Initializes HelpScout Beacon by adding the script to the page
 * Safe to call multiple times - will only initialize once
 */
export function initBeacon() {
  if (sfChat.enabled) return sfChat.init()

  // Skip if already initialized
  if (window.Beacon) return

  const beaconScriptContent = `!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});`
  const initScriptContent = `window.Beacon('init', '${BEACON_ID}');`

  const beaconScript = createScriptTag(beaconScriptContent)
  const initScript = createScriptTag(initScriptContent)

  document.head.appendChild(beaconScript)
  document.head.appendChild(initScript)
}

/**
 * Sets session data for Beacon (context or error data)
 * All fields are flat/top-level so support can search and filter
 *
 * Context fields:
 *   - 'Flow': 'Direct Join' | 'LTI 1.1' | 'LTI 1.3'
 *   - 'Course': Course title
 *   - 'Domain': Canvas domain or platform ISS
 *   - 'Token': Enrollment/membership token
 *   - 'Email': Student email
 *   - 'Course Id': Canvas course ID (LTI 1.1 only)
 *
 * Error fields:
 *   - 'User Message': User-friendly message (most useful for support)
 *   - 'Error Context': Where the error occurred
 *   - 'Error Code': Backend error code
 *   - 'Error Message': Technical details (optional, from API)
 *   - 'HTTP Status': HTTP status code
 *   - 'Access Code': The code the student typed (access code errors only)
 *   - 'Term': The term a stale join link belonged to (ended-term errors only)
 *
 * @param {object} data - Key-value pairs to set
 */
export function setBeaconData(data) {
  if (sfChat.enabled) return sfChat.setData(data)

  if (window.Beacon) {
    window.Beacon('session-data', data)
  }
}

/**
 * Clears error data from Beacon session
 * Call this on successful operations to reset the support context
 *
 * HelpScout doesn't document whether repeated 'session-data' calls merge keys
 * or replace the whole set, so this is written to be correct under either:
 * pass the view's flow/course/token context fields and they are re-asserted
 * alongside the nulled error fields.
 *
 * @param {object} context - The view's flow/course/token context fields
 */
export function clearBeaconError(context = {}) {
  setBeaconData({
    ...context,
    'User Message': null,
    'Error Context': null,
    'Error Code': null,
    'Error Message': null,
    'HTTP Status': null,
    'Access Code': null,
    'Term': null,
    'Enrollment Blocked': null
  })
}

/**
 * Formats the term an ended join link belonged to, for support's eyes only.
 *
 * The student's message deliberately names no term (support's wording call on
 * TWA-578), so this Beacon field is the only place the term surfaces — it is
 * what tells support a stale link apart from a genuinely unpublished course
 * without looking the context up in the courses sheet. Canvas can leave the
 * name or either date unset, so every part is optional and an empty term
 * reports nothing rather than stray punctuation.
 *
 * @param {object|null} term - { name, start_at, end_at }, as sent by get_course
 * @returns {string|null} e.g. 'Winter/Spring 2026 (01/01/2026 - 06/30/2026)'
 */
function formatTerm(term) {
  if (!term) return null

  const range = [term.start_at, term.end_at].filter(Boolean).join(' - ')
  return [term.name, range && `(${range})`].filter(Boolean).join(' ') || null
}

/**
 * Reports an API error to Beacon so support sees what went wrong before the
 * student even types a message. Pass the view's context fields back in —
 * HelpScout doesn't document whether 'session-data' merges or replaces, so
 * every report carries the full set to be correct under either.
 *
 * @param {Error} error - Axios error object
 * @param {string} context - Call-site label, e.g. 'Page Load Error'
 * @param {object} beaconContext - The view's flow/course/token context fields
 * @param {string|null} accessCode - The code the student typed (access code errors);
 *   null on other errors so a previously reported code doesn't linger
 * @returns {object} The error fields that were set (Verify shows them in its debug panel)
 */
export function reportErrorToBeacon(error, context, beaconContext = {}, accessCode = null) {
  const beaconError = {
    'User Message': getErrorMessage(error),
    'Error Context': context,
    'Error Code': error.response?.data?.error?.code || null,
    // ?? null so network errors (no response) still write the keys — undefined
    // drops out of the payload and would leave a prior attempt's values behind
    'Error Message': error.response?.data?.error?.message ?? null,
    'HTTP Status': error.response?.status ?? null,
    'Access Code': accessCode,
    'Term': formatTerm(error.response?.data?.term)
  }
  setBeaconData({ ...beaconContext, ...beaconError })
  return beaconError
}

/**
 * Reports a client-side validation failure (no HTTP error object) to Beacon.
 *
 * @param {string} message - The message shown to the user
 * @param {string} context - Call-site label, e.g. 'Missing Token'
 * @param {object} beaconContext - The view's flow/course/token context fields
 * @param {string|null} accessCode - The code the student typed ('' when the field
 *   was left empty); null on non-code validation errors
 * @returns {object} The error fields that were set
 */
export function reportValidationErrorToBeacon(message, context, beaconContext = {}, accessCode = null) {
  const beaconError = {
    'User Message': message,
    'Error Context': context,
    'Error Code': 'frontend_validation',
    'Error Message': null,
    'HTTP Status': null,
    'Access Code': accessCode,
    'Term': null
  }
  setBeaconData({ ...beaconContext, ...beaconError })
  return beaconError
}

/**
 * Opens the Beacon support widget
 */
export function openBeacon() {
  if (sfChat.enabled) return sfChat.open()

  if (window.Beacon) {
    window.Beacon('open')
  }
}

/**
 * Identifies the user to Beacon
 * @param {string} email - User's email address
 */
export function identifyBeaconUser(email) {
  if (sfChat.enabled) return sfChat.identify(email)

  if (window.Beacon) {
    window.Beacon('identify', { email })
  }
}
